/**
 * IMPORTS
 */
import AxiosService from "@/infra/http/axios";
import { IManagerCreditsRepository } from "@/interface/credits";
import {
  IParametrosConsultaCobrancaCreditoCliente,
  IParametrosConsultaExtratoMovimentacoesCreditoCliente,
} from "@/interface/extrato-credito-cliente";

const managerCreditsRepository: IManagerCreditsRepository = {
  async getBuscarExtratoMovimentacoesCreditoPorClienteId<T>(
    accessToken: string,
    clienteId: number,
    parametrosConsulta: IParametrosConsultaExtratoMovimentacoesCreditoCliente
  ): Promise<T | null> {
    const instanciaHttp = await AxiosService.createAxiosInstance(accessToken);

    const queryParams = new URLSearchParams();
    queryParams.set("page", String(parametrosConsulta.paginaAtual));
    queryParams.set("per_page", String(parametrosConsulta.itensPorPagina));

    if (parametrosConsulta.produtoIdFiltro != null) {
      queryParams.set("produto_id", String(parametrosConsulta.produtoIdFiltro));
    }
    if (parametrosConsulta.tipoOperacaoFiltro) {
      queryParams.set("tipo_operacao", parametrosConsulta.tipoOperacaoFiltro);
    }
    if (parametrosConsulta.direcaoMovimentoFiltro) {
      queryParams.set("direcao_movimento", parametrosConsulta.direcaoMovimentoFiltro);
    }
    if (parametrosConsulta.dataInicioFiltro) {
      queryParams.set("data_inicio", parametrosConsulta.dataInicioFiltro);
    }
    if (parametrosConsulta.dataFimFiltro) {
      queryParams.set("data_fim", parametrosConsulta.dataFimFiltro);
    }
    if (parametrosConsulta.ordenarPor) {
      queryParams.set("sort_by", parametrosConsulta.ordenarPor);
    }
    if (parametrosConsulta.ordemClassificacao) {
      queryParams.set("order", parametrosConsulta.ordemClassificacao);
    }

    const queryString = queryParams.toString();
    const caminhoRequisicao = `vendas_creditos/cliente/${clienteId}/extrato${queryString ? `?${queryString}` : ""}`;

    const respostaExtrato = await instanciaHttp.get(caminhoRequisicao, {
      headers: {
        "content-type": "application/json; charset=UTF-8",
        accept: "*/*",
      },
    });

    return respostaExtrato?.data;
  },

  async getBuscarCobrancaCreditosPorClienteId<T>(
    accessToken: string,
    clienteId: number,
    parametrosConsulta: IParametrosConsultaCobrancaCreditoCliente
  ): Promise<T | null> {
    const instanciaHttp = await AxiosService.createAxiosInstance(accessToken);

    const queryParams = new URLSearchParams();
    queryParams.set("page", String(parametrosConsulta.paginaAtual ?? 1));
    queryParams.set("per_page", String(parametrosConsulta.itensPorPagina ?? 1));

    if (parametrosConsulta.produtoIdFiltro != null) {
      queryParams.set("produto_id", String(parametrosConsulta.produtoIdFiltro));
    }
    if (parametrosConsulta.dataInicioFiltro) {
      queryParams.set("data_inicio", parametrosConsulta.dataInicioFiltro);
    }
    if (parametrosConsulta.dataFimFiltro) {
      queryParams.set("data_fim", parametrosConsulta.dataFimFiltro);
    }

    const queryString = queryParams.toString();
    const caminhoRequisicao = `vendas_creditos/cliente/${clienteId}/cobranca${queryString ? `?${queryString}` : ""}`;

    const respostaCobranca = await instanciaHttp.get(caminhoRequisicao, {
      headers: {
        "content-type": "application/json; charset=UTF-8",
        accept: "*/*",
      },
    });

    return respostaCobranca?.data;
  },

  /**
   * Cobrança de créditos do próprio cliente logado (sem valores em R$).
   * O cliente é resolvido pela API a partir do token.
   */
  async getBuscarMinhaCobrancaCreditos<T>(
    accessToken: string,
    parametrosConsulta: IParametrosConsultaCobrancaCreditoCliente
  ): Promise<T | null> {
    const instanciaHttp = await AxiosService.createAxiosInstance(accessToken);

    const queryParams = new URLSearchParams();
    queryParams.set("page", String(parametrosConsulta.paginaAtual ?? 1));
    queryParams.set("per_page", String(parametrosConsulta.itensPorPagina ?? 10));

    if (parametrosConsulta.produtoIdFiltro != null) {
      queryParams.set("produto_id", String(parametrosConsulta.produtoIdFiltro));
    }
    if (parametrosConsulta.dataInicioFiltro) {
      queryParams.set("data_inicio", parametrosConsulta.dataInicioFiltro);
    }
    if (parametrosConsulta.dataFimFiltro) {
      queryParams.set("data_fim", parametrosConsulta.dataFimFiltro);
    }

    const respostaCobranca = await instanciaHttp.get(
      `vendas_creditos/minha-cobranca?${queryParams.toString()}`,
      {
        headers: {
          "content-type": "application/json; charset=UTF-8",
          accept: "*/*",
        },
      }
    );

    return respostaCobranca?.data;
  },

  /**
   * Função para buscar tansacoes de um cliente
   * @param accessToken
   * @returns Promise<any | null>
   */
  async getBuscarVendasCreditos<T>(
    accessToken: string,
    user_cliente_id: number,
    currentPage: number = 1,
    itemsPerPage: number = 5,
    searchItem: string
  ): Promise<T | null> {
    const Instance = await AxiosService.createAxiosInstance(accessToken);

    const credits = await Instance.get(
      `vendas_creditos/cliente/${user_cliente_id}?page=${currentPage}&per_page=${itemsPerPage}&search=${searchItem}`,
      {
        headers: {
          "content-type": "application/json; charset=UTF-8",
          accept: "*/*",
        },
      }
    );

    return credits?.data;
  },

  async getBuscarTotalVendasCreditosPorCliente<T>(
    accessToken: string,
    user_cliente_id: number
  ): Promise<T | null> {
    const Instance = await AxiosService.createAxiosInstance(accessToken);

    const credits = await Instance.get(`vendas_creditos/cliente/${user_cliente_id}/creditos`, {
      headers: {
        "content-type": "application/json; charset=UTF-8",
        accept: "*/*",
      },
    });

    return credits?.data;
  },
  async criarNovaVendaCreditos(accessToken, payload) {
    const Instance = await AxiosService.createAxiosInstance(accessToken);

    const customers = await Instance.post(`vendas_creditos`, payload, {
      headers: {
        "content-type": "application/json; charset=UTF-8",
        accept: "*/*",
      },
    });

    return customers?.data;
  },

  async atualizarVendaCredito(accessToken, venda_credito_id, payload) {
    const Instance = await AxiosService.createAxiosInstance(accessToken);

    const vendaCreditoAtualizado = await Instance.put(
      `vendas_creditos/${venda_credito_id}`,
      payload,
      {
        headers: {
          "content-type": "application/json; charset=UTF-8",
          accept: "*/*",
        },
      }
    );

    return vendaCreditoAtualizado?.data;
  },

  async cancelarVendaCreditos(accessToken, venda_credito_id) {
    const Instance = await AxiosService.createAxiosInstance(accessToken);

    const vendaCreditoCancelado = await Instance.post(
      `vendas_creditos/${venda_credito_id}/cancelar`,
      {
        headers: {
          "content-type": "application/json; charset=UTF-8",
          accept: "*/*",
        },
      }
    );

    return vendaCreditoCancelado?.data;
  },
};

/**
 * EXPORTS
 */
export { managerCreditsRepository };
